import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface GoogleReview {
  id: string;
  author_name: string;
  profile_photo_url?: string;
  rating: number;
  text: string;
  relative_time_description: string;
  language: string;
  source: string;
}

export interface GoogleReviewsData {
  place_name: string;
  overall_rating: number;
  total_ratings: number;
  reviews: GoogleReview[];
  google_maps_url: string;
}

export function useGoogleReviews() {
  const [data, setData] = useState<GoogleReviewsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError(null);

      const [summaryRes, reviewsRes] = await Promise.all([
        supabase.from('google_reviews_summary').select('*').limit(1).single(),
        supabase.from('google_reviews').select('*').eq('is_visible', true).order('display_order', { ascending: true }),
      ]);

      if (summaryRes.error) throw new Error(summaryRes.error.message);
      if (reviewsRes.error) throw new Error(reviewsRes.error.message);

      setData({
        place_name: summaryRes.data?.place_name || 'Punto Hispano',
        overall_rating: Number(summaryRes.data?.overall_rating) || 4.9,
        total_ratings: summaryRes.data?.total_ratings || 0,
        google_maps_url: summaryRes.data?.google_maps_url || 'https://g.page/r/CW1vFSlnKC89EAE/review',
        reviews: (reviewsRes.data || []).map((r: any) => ({
          id: r.id,
          author_name: r.author_name,
          profile_photo_url: r.profile_photo_url,
          rating: r.rating,
          text: r.text,
          relative_time_description: r.relative_time_description,
          language: r.language,
          source: r.source,
        })),
      });
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar reseñas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  return { data, loading, error, refetch: fetchReviews };
}
